// @flow
// Vendor libs
import * as Cookie from 'app/packages/cookie-utils';
import CONST from 'app/common/lib/Const';
import storage from 'app/common/lib/Storage';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { InterestObj } from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import type {
  GridPin,
  NewsHubPin,
  NewsHubDetailPin,
  CloseupPin,
  DetailedPin,
  RepinPin,
  LandingPageStoryPin,
  BoardSectionPreviewPin,
} from 'app/packages/pinterest-flow-types/pin';

type Pin = GridPin &
  NewsHubPin &
  CloseupPin &
  DetailedPin &
  NewsHubDetailPin &
  RepinPin &
  BoardSectionPreviewPin &
  LandingPageStoryPin;

const ONE_WEEK_SECS = 60 * 60 * 24 * 7;

const timestampExpired = (tsKey, period = ONE_WEEK_SECS) => {
  const ts = window.localStorage.getItem(tsKey);
  return ts && Date.now() - parseInt(ts, 10) > period * 1000;
};

const UnauthUsage = {
  /**
   * Log to window.localStorage the pathname of the currently displayed page along with the timestamp.
   */
  logUrl() {
    // Don't log refreshes
    if (
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_LAST_VISITED_PAGE) !==
      window.location.pathname
    ) {
      storage.localStorage.prependItem(
        PCONST.LOCALSTORAGE_VISITED_PAGES,
        { path: window.location.pathname, ts: Date.now() },
        CONST.MAX_STORED_VISITED_PAGES,
      );
      try {
        storage.localStorage.setItem(
          PCONST.LOCALSTORAGE_LAST_VISITED_PAGE,
          window.location.pathname,
        );
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    }
  },
  /**
   * Log to window.localStorage the pathname of the currently displayed page along with the timestamp
   * and first pin image signature.
   */
  logUrlAndFirstPinImageSig(imageSig: string) {
    // Don't log refreshes
    if (
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_LAST_VISITED_PAGE) ===
      window.location.pathname
    ) {
      return;
    }

    const pinInfo = {
      path: window.location.pathname,
      ts: Date.now(),
      first_pin_image_signature: imageSig || '',
    };

    storage.localStorage.prependItem(
      PCONST.LOCALSTORAGE_VISITED_PAGES,
      pinInfo,
      CONST.MAX_STORED_VISITED_PAGES,
    );

    storage.sessionStorage.prependItem(
      PCONST.SESSIONSTORAGE_VISITED_PAGES,
      pinInfo,
      CONST.MAX_STORED_VISITED_PAGES,
    );

    trackRegisterAction(
      'unauth.web.session.visited_pins.' +
        storage.sessionStorage.getItem(PCONST.SESSIONSTORAGE_VISITED_PAGES).length,
    );

    storage.localStorage.prependItem(
      PCONST.LOCALSTORAGE_VISITED_PAGES_BEFORE_LOGIN,
      pinInfo,
      CONST.MAX_STORED_VISITED_PAGES,
    );

    try {
      storage.localStorage.setItem(PCONST.LOCALSTORAGE_LAST_VISITED_PAGE, window.location.pathname);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  },

  /**
   * Currently only called from PinLandingCloseup and PinFeedbackPageContent.
   * Log PLPs / shared pins visited for usage during NUX
   */
  logLastVisitedPinLandingPage(pin: Pin, isSharedPin: boolean) {
    if (!pin || __SERVER__) {
      return;
    }

    // Don't log refreshes
    const prevPages = storage.localStorage.getItem(PCONST.LOCALSTORAGE_VISITED_PLPS_BEFORE_LOGIN);
    if (prevPages) {
      for (const page of prevPages) {
        if (page && page.id === pin.id) {
          return;
        }
      }
    }

    const pinInfo = {
      ts: Date.now(),
      path: window.location.pathname,
      id: pin.id,
      image_signature: pin.image_signature,
      is_shared_pin: isSharedPin,
    };
    storage.localStorage.prependItem(
      PCONST.LOCALSTORAGE_VISITED_PLPS_BEFORE_LOGIN,
      pinInfo,
      CONST.MAX_STORED_VISITED_PIN_PAGES,
    );
  },

  /**
   * Currently only called from UnauthHybridBoardPage.
   * Log BLPs visited for usage during NUX, for now just logging the first pin in the board
   */
  logLastVisitedBoardLandingPage(pin: {
    id: string,
    image_signature: string,
    board: { id: string },
  }) {
    if (!pin || !pin.board || __SERVER__) {
      return;
    }

    // Don't log refreshes
    const prevPages = storage.localStorage.getItem(PCONST.LOCALSTORAGE_VISITED_BLPS_BEFORE_LOGIN);
    if (prevPages) {
      for (const page of prevPages) {
        if (page && page.board_id === pin.board.id) {
          return;
        }
      }
    }

    const boardInfo = {
      ts: Date.now(),
      board_id: pin.board.id,
      first_pin_id: pin.id,
      first_pin_image_signature: pin.image_signature,
    };
    storage.localStorage.prependItem(
      PCONST.LOCALSTORAGE_VISITED_BLPS_BEFORE_LOGIN,
      boardInfo,
      CONST.MAX_STORED_VISITED_PIN_PAGES,
    );
  },

  /**
   * Log the Pin into the visited pages on localStorage for interactions with the PinIt/Save/View CTA
   */
  logPinCTAClick(pinPath: string) {
    storage.localStorage.prependItem(
      PCONST.LOCALSTORAGE_VISITED_PAGES,
      {
        path: pinPath,
        ts: Date.now(),
        actions: [PCONST.thrift.URSVisitedPageAction.PIN_HOVER_CTA_CLICK],
      },
      CONST.MAX_STORED_VISITED_PAGES,
    );
  },

  /**
   * Log to Cookie the last 10 pin page pin IDs
   * Set in cookie to improve server-side recommendations
   */
  logPinPage(pin: Pin) {
    const lastVisitedPinPageCookie = Cookie.get(PCONST.COOKIE_LAST_VISITED_PIN_PAGES);

    if (!pin || !pin.id) {
      return;
    }

    if (lastVisitedPinPageCookie) {
      const lastVisitedPinPages = JSON.parse(lastVisitedPinPageCookie);
      if (lastVisitedPinPages.length >= CONST.MAX_STORED_VISITED_PIN_PAGES) {
        // get rid of the first/oldest element in the list
        lastVisitedPinPages.shift();
      }
      lastVisitedPinPages.push(pin.id);
      Cookie.set(PCONST.COOKIE_LAST_VISITED_PIN_PAGES, JSON.stringify(lastVisitedPinPages));

      trackRegisterAction('unauth_pin_cookie.' + lastVisitedPinPages.length);
    } else {
      Cookie.set(PCONST.COOKIE_LAST_VISITED_PIN_PAGES, JSON.stringify([pin.id]));
    }
  },

  /**
   * Logs a pin closeup for AO multivisit to build a feed based on what a user has clicked
   * i.e. after 5 unauth pin clicks, user clicks on multivisit tray to see related pins
   * @param {pin} pin - pin clicked
   */
  logPinCloseup(pin: { id: string, image: string }) {
    // save click timestamp and pin image url
    if (!pin || !pin.id) {
      return;
    }
    const pinTrayObj: Object = {
      id: pin.id,
      ts: Date.now(),
    };
    if (pin.image) {
      pinTrayObj.image = pin.image;
    }
    // save to local, or overwrite if already exists
    let closeupPins = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_PIN_CLOSEUP);
    if (!closeupPins) {
      closeupPins = [pinTrayObj];
    } else {
      // fix for users who had a bad cookie - clear the cookie if it's bad
      if (Array.isArray(closeupPins[0])) {
        storage.localStorage.removeItem(PCONST.LOCALSTORAGE_UNAUTH_PIN_CLOSEUP);
        closeupPins = [];
      }
      for (let i = 0; i < closeupPins.length; i += 1) {
        if (closeupPins[i].id === pinTrayObj.id) {
          closeupPins.splice(i, 1);
        }
        // if we store max amount of pins - remove oldest pin (first element)
        // $FlowFixMe: MAX_STORED_VISITED_PINS missing in object literal
        if (i >= CONST.MAX_STORED_VISITED_PINS) {
          closeupPins.shift();
        }
      }
      closeupPins.push(pinTrayObj);
    }
    // update local storage
    try {
      storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_PIN_CLOSEUP, closeupPins);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  },

  /**
   * Logs a topic click for AO multivisit to build a feed based on what a user has clicked
   * i.e. after 5 unauth topic clicks, user clicks on multivisit tray to see related pins
   * @param {topic} topic - topic clicked
   */
  logTopicClick(topic: InterestObj) {
    if (!topic || !topic.id || !topic.name) {
      return;
    }
    // save topic (interest) timestamp, image url, and name
    const topicTrayObj: Object = {
      id: topic.id,
      ts: Date.now(),
      name: topic.name,
    };
    // only safe way to get image - see Interest.js
    let imageUrl = '';
    if (topic.images) {
      const imagesKeys = Object.keys(topic.images);
      if (imagesKeys.length > 0 && topic.images[imagesKeys[0]].url) {
        imageUrl = topic.images[imagesKeys[0]].url;
      }
    } else if (topic.image) {
      imageUrl = topic.image;
    }
    topicTrayObj.imageUrl = imageUrl;
    if (topic.background_color) {
      topicTrayObj.backgroundColor = topic.background_color;
    }
    // save to local, or push to most recent click if already exists
    let clickedTopics = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_TOPIC_VISIT);
    if (!clickedTopics) {
      clickedTopics = [topicTrayObj];
    } else {
      // fix for users who had a bad cookie - clear the cookie if it's bad
      if (Array.isArray(clickedTopics[0])) {
        storage.localStorage.removeItem(PCONST.LOCALSTORAGE_UNAUTH_TOPIC_VISIT);
        clickedTopics = [];
      }
      for (let i = 0; i < clickedTopics.length; i += 1) {
        if (clickedTopics[i].id === topicTrayObj.id) {
          clickedTopics.splice(i, 1);
        }
        // if we store max amount of topics - remove oldest topic (first element)
        // $FlowFixMe: MAX_STORED_VISITED_TOPICS missing in object literal
        if (i >= CONST.MAX_STORED_VISITED_TOPICS) {
          clickedTopics.shift();
        }
      }
      // push topic to the end
      clickedTopics.push(topicTrayObj);
    }
    // update storage
    try {
      storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_TOPIC_VISIT, clickedTopics);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  },

  /**
   * Return true if at least one pin closeup click is logged
   */
  hasClickedPins() {
    const clickedPins = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_PIN_CLOSEUP);
    return clickedPins && clickedPins.length > 0;
  },

  /**
   * Returns most recent pins for AO multivisit
   * Sorted from most recent -> least recent
   * @param {int} amount - number of pins to return
   * will return all pins if amount >= pins in local storage
   */
  getRecentClickedPins(amount: number): Array<Pin> {
    const clickedPins = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_PIN_CLOSEUP);
    return this._getRecentItems(amount, clickedPins);
  },

  /**
   * Returns most recent topics for AO multivisit
   * Sorted from most recent -> least recent
   * @param {int} amount - number of topics to return
   * will return all topics if amount >= topics in local storage
   */
  getRecentClickedTopics(amount: number): Array<InterestObj> {
    const clickedTopics = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_TOPIC_VISIT);
    return this._getRecentItems(amount, clickedTopics);
  },

  _getRecentItems(amount: number, arr: Array<*>): Array<*> {
    if (!arr) {
      return [];
    }
    if (arr.length <= amount) {
      return arr;
    }
    return arr.slice(arr.length - amount);
  },

  getLastVisitedFeedInfo() {
    const last_visited_url = storage.localStorage.getItem(PCONST.LOCALSTORAGE_LAST_VISITED_PAGE);
    let pathParts = [];
    if (last_visited_url) {
      pathParts = last_visited_url.split('/');
    } else {
      return null;
    }
    if (pathParts.length < 4) {
      return null;
    }

    const firstSection = pathParts[1];
    const secondSection = pathParts[2];
    switch (firstSection) {
      case 'pin':
        return {
          pageType: 'pin',
          id: secondSection,
        };
      case 'explore':
        return {
          pageType: 'explore',
          id: secondSection,
        };
      default:
        return null;
    }
  },

  logUnauthImageFeed(pins: Array<Pin>) {
    const images = pins
      .map((pin) => pin.images && pin.images['170x'] && pin.images['170x'].url)
      .filter((imageUrl) => !!imageUrl);
    if (images.length) {
      try {
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_RECENT_IMAGE_FEED, images);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    }
  },

  getUnauthImageFeed() {
    return storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_RECENT_IMAGE_FEED) || [];
  },

  logUnauthPins(pins: Array<{ type: string, image_signature: string }>) {
    if (pins.length) {
      try {
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_PINS, pins);
        storage.sessionStorage.prependItem(PCONST.SESSIONSTORAGE_UNAUTH_PINS, pins[0]);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    }
  },

  getUnauthPins() {
    return storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_PINS) || [];
  },

  getUnauthSessionsPins() {
    return storage.sessionStorage.getItem(PCONST.SESSIONSTORAGE_UNAUTH_PINS) || [];
  },

  getUnauthCloseupCount(expiration: number = ONE_WEEK_SECS): number {
    const count = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLOSEUP);

    if (timestampExpired(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLOSEUP_TS, expiration) || !count) {
      return 0;
    }
    return parseInt(count, 10) || 0;
  },

  logUnauthHybridCloseup(expiration: number = ONE_WEEK_SECS) {
    const count = this.getUnauthCloseupCount();

    if (timestampExpired(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLOSEUP_TS, expiration) || !count) {
      try {
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLOSEUP, 1);
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLOSEUP_TS, Date.now());
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    } else {
      try {
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLOSEUP, count + 1);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    }
  },

  getUnauthClickthroughCount(expiration: number = ONE_WEEK_SECS): number {
    const count = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLICKTHROUGH);

    if (timestampExpired(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLICKTHROUGH_TS, expiration) || !count) {
      return 0;
    }
    return parseInt(count, 10) || 0;
  },

  logUnauthHybridClickthrough(expiration: number = ONE_WEEK_SECS) {
    const count = this.getUnauthClickthroughCount();
    if (timestampExpired(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLICKTHROUGH_TS, expiration) || !count) {
      try {
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLICKTHROUGH, 1);
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLICKTHROUGH_TS, Date.now());
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    } else {
      try {
        storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_CLICKTHROUGH, count + 1);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    }
  },

  logLoginSignupModalView() {
    try {
      storage.localStorage.setItem(
        PCONST.LOCALSTORAGE_UNAUTH_HYBRID_LOGIN_SIGNUP_MODAL_VIEW,
        Date.now(),
      );
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  },

  getLoginSignupModalView() {
    return (
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_HYBRID_LOGIN_SIGNUP_MODAL_VIEW) || 0
    );
  },
};

export default UnauthUsage;
